import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { useContestInvitesStats } from '~/domains/invites-manager/hooks/useContestInvitesStats';
import { isLoadingFixedForDisabledQuery } from '~/domains/common/utils/isLoadingFixedForEnabled';
import { STALE_TIME } from '~/domains/contest/constants/query';
import {
  getChannelFollowees,
  getChannelFollowers,
  getChannelFollowersPages,
} from '~/services/commissioner/commissioner-service.service.api';

type UseChannelFollowersProps = {
  channelId: string;
  contestId?: string;
  query?: string;
  enabled?: boolean;
};

export function useChannelFollowers({
  channelId,
  contestId,
  enabled = true,
  query,
}: UseChannelFollowersProps) {
  // eslint-disable-next-line local-rules/require-query-stale-time
  const { data, fetchNextPage, hasNextPage, fetchStatus, isLoading, isFetching } = useInfiniteQuery(
    {
      enabled: Boolean(channelId) && enabled,
      queryKey: ['commissioner/followers', channelId, contestId, query, 'followers'],
      queryFn: ({ pageParam = 1 }) =>
        getChannelFollowers(channelId, {
          page: pageParam,
          ...(query ? { q: query } : {}),
          contestId,
        }),
      getNextPageParam: ({ totalPages, page }) => {
        if (page < totalPages) {
          return page + 1;
        }
      },
      keepPreviousData: true,
    }
  );

  const followersData = data?.pages.flatMap((page) => page.data) ?? [];

  return {
    totalCount: data?.pages[0]?.totalCount,
    followersData,
    fetchNextPage,
    hasNextPage,
    isLoading: isLoadingFixedForDisabledQuery(isLoading, fetchStatus),
    isFetching,
  };
}

export function useAlreadyInvitedFollowersCount({
  channelId,
  contestId,
  enabled = true,
}: UseChannelFollowersProps) {
  const { data: invitesStatsData, isLoading: isInvitesStatsLoading } = useContestInvitesStats({
    contestId,
  });
  const invitesProcessing = invitesStatsData?.invitesProcessing;

  // eslint-disable-next-line local-rules/require-query-stale-time
  const { data, isLoading } = useQuery({
    enabled: Boolean(channelId) && enabled,
    queryKey: ['commissioner/followers', channelId, contestId, 'alreadyInvited'],
    queryFn: () =>
      getChannelFollowers(channelId, { contestId, contestInviteStatuses: ['ACCEPTED', 'PENDING'] }),
    refetchInterval: () => (invitesProcessing ? 2000 : false),
  });

  return {
    alreadyInvitedFollowersCount: data?.totalCount ?? 0,
    isLoading: isLoading || isInvitesStatsLoading,
  };
}

export function useChannelFollowees({ channelId, enabled, query }: UseChannelFollowersProps) {
  const { data, fetchNextPage, hasNextPage, fetchStatus, isLoading, isFetching, isFetchingNextPage } =
    useInfiniteQuery({
      enabled: Boolean(channelId) && enabled,
      queryKey: ['commissioner/followers', channelId, query, 'followees'],
      queryFn: ({ pageParam = 1 }) =>
        getChannelFollowees(channelId, { page: pageParam, ...(query ? { q: query } : {}) }),
      getNextPageParam: ({ totalPages, page }) => {
        if (page < totalPages) {
          return page + 1;
        }
      },
      keepPreviousData: true,
    });

  const followeesData = data?.pages.flatMap((page) => page.data) ?? [];

  const result = {
    followeesData,
    fetchNextPage,
    hasNextPage,
    isLoading: isLoadingFixedForDisabledQuery(isLoading, fetchStatus),
    isFetching,
    isFetchingNextPage,
  };
  return result;
}

type UseChannelFollowersPagesProps = {
  channelId: string;
  contestId?: string;
  query?: string;
  offset?: number;
  limit?: number;
};

const PAGE_SIZE = 20;

export function useChannelFollowersPages({
  channelId,
  contestId,
  offset = 0,
  limit = PAGE_SIZE,
  query,
}: UseChannelFollowersPagesProps) {
  const { data, fetchStatus, isLoading, isFetching } = useQuery({
    enabled: Boolean(channelId) && !!channelId,
    queryKey: [
      'commissioner/followers',
      channelId,
      contestId,
      query,
      limit,
      offset,
      'followers-pages',
    ],
    queryFn: () =>
      getChannelFollowersPages({
        channelId,
        offset,
        limit,
        ...(query ? { q: query } : {}),
      }),
    staleTime: STALE_TIME,
  });

  const followersData = (data?.data ?? []).map((user) => ({
    ...user,
    id: user.userId,
    avatarUrl: user.avatarUrl,
    contestId,
    unifiedUserId: user.userId,
    email: '',
  }));

  return {
    totalCount: data?.totalCount,
    followersData,
    isLoading: isLoadingFixedForDisabledQuery(isLoading, fetchStatus),
    isFetching,
  };
}
